import { ReactElement, useEffect } from 'react'
import { DrawModePane, OpeningForm } from '@structuralPlanningComponents'
import { useCameraStore } from '@modugen/ui-toolkit'
import { CorneredSection } from '@ui2/structure'
import { useEditElementStore } from '@editorStores'
import { useIfcMode } from '@editorHooks'

/**
 * Drawer panel of the "in Draufsicht bearbeiten" tool on the non-guided IFC page — the importer
 * counterpart of Structural Planning's `draw-openings` mode. Reuses Structural Planning's
 * `OpeningForm` for the wall currently active (`useIFCImportOpeningDrawing` injects the matching
 * `OrthographicWall`/`OpeningsDrawer` scene tools). Saving/deleting deselects only the opening (not
 * the wall), so the tool stays active and further openings can be drawn on the same wall.
 */
const IFCOpeningDrawPanel = (): ReactElement => {
  const activeElement = useEditElementStore(state => state.activeElement)
  const activeOpening = useEditElementStore(state => state.activeOpening)
  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const { setIfcMode } = useIfcMode()

  // The tool is bound to one wall — planar selection is suppressed while it is active, so without
  // a wall (deselected via Escape, a storey change, …) it can do nothing. Exit the mode instead of
  // showing OpeningForm's "Wand anwählen" placeholder, which the importer can't act on.
  useEffect(() => {
    if (!activeElement) setIfcMode(null)
  }, [activeElement, setIfcMode])

  return (
    <CorneredSection>
      <DrawModePane
        elementLabel="Öffnungen"
        onEnd={() => setIfcMode(null)}
        data-cy="btn-exit-ifc-draw-openings-mode"
      >
        <OpeningForm
          selectedWallId={activeElement}
          selectedOpeningId={activeOpening}
          deselectWall={() => setActiveOpening(null)}
          setElements={() => setActiveOpening(null)}
          mode={isOrthographic ? '2D' : '3D'}
          showDeselectButton
        />
      </DrawModePane>
    </CorneredSection>
  )
}

export default IFCOpeningDrawPanel
